"use client";

/**
 * Language switcher — dropdown with EN / HT / ES options.
 * SYS3: persists to localStorage under key petci_demo_locale.
 * a11y: label-in-name compliant (aria-label starts with visible text);
 * option role on clickable li (no nested button).
 */
import { useState, useRef, useCallback } from "react";
import { Globe } from "lucide-react";
import { useLocaleStore, LOCALES, LOCALE_LABELS, LOCALE_NAMES } from "@/lib/locale";
import { useDismissable } from "@petci/shared-ui";

/**
 * `variant` styles the trigger only (the dropdown popover stays a white surface,
 * legible on any background): "light" for on-white chrome (PublicHeader, AppNav);
 * "onDark" for the navy hero, where the availability line hosts the control (L2).
 */
export default function LanguageSwitcher({
  variant = "light",
}: {
  variant?: "light" | "onDark";
} = {}) {
  const { locale, setLocale } = useLocaleStore();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const closeSwitcher = useCallback(() => setOpen(false), []);
  useDismissable({ open, onClose: closeSwitcher, ref: containerRef });

  const triggerClass =
    variant === "onDark"
      ? "inline-flex items-center gap-1.5 text-xs font-medium text-white/90 hover:text-white transition-colors px-2.5 py-1.5 rounded-md ring-1 ring-white/25 hover:bg-white/10"
      : "inline-flex items-center gap-1.5 text-xs font-medium text-neutral-600 hover:text-brand-primary transition-colors px-2 py-1.5 rounded-md hover:bg-neutral-100";

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const idx = LOCALES.indexOf(locale);
      const nextIdx = e.key === "ArrowDown"
        ? (idx + 1) % LOCALES.length
        : (idx - 1 + LOCALES.length) % LOCALES.length;
      const next = LOCALES[nextIdx];
      if (next) setLocale(next);
    }
  }

  return (
    <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`${LOCALE_LABELS[locale]} — ${LOCALE_NAMES[locale]}, select language`}
        className={triggerClass}
      >
        <Globe size={14} aria-hidden />
        {LOCALE_LABELS[locale]}
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={`${LOCALE_LABELS[locale]} — select language`}
          className="absolute right-0 top-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 min-w-[140px] z-50"
        >
          {LOCALES.map((loc) => (
            <li
              key={loc}
              role="option"
              aria-selected={loc === locale}
              onClick={() => { setLocale(loc); setOpen(false); }}
              tabIndex={0}
              className={`cursor-pointer w-full text-left px-3 py-2 text-sm transition-colors ${
                loc === locale
                  ? "bg-brand-primary/10 text-brand-primary font-medium"
                  : "text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              <span className="font-medium">{LOCALE_LABELS[loc]}</span>
              <span className="ml-2 text-neutral-500">{LOCALE_NAMES[loc]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
